import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Play, Users, Lightbulb, CheckCircle } from 'lucide-react';
import { newsletters } from '../data/mock';
import PDFViewer from '../components/PDFViewer';

const NewsletterDetail = () => {
  const { id } = useParams();
  const newsletter = newsletters.find((n) => n.id === id);

  // Scroll to top when page loads or id changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!newsletter) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-20 h-20 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-6">
            <FileText className="w-10 h-10 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-display text-foreground mb-4">Newsletter Not Found</h1>
          <Link 
            to="/newsletters" 
            className="text-primary hover:text-[hsl(38,70%,60%)] inline-flex items-center gap-2 transition-smooth"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Newsletter Library
          </Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Back Navigation */}
      <div className="bg-card border-b border-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-4">
          <Link 
            to="/newsletters" 
            className="text-sm text-muted-foreground hover:text-foreground transition-smooth inline-flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Newsletter Library
          </Link>
        </div>
      </div>

      {/* Header Section with Image */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 gradient-hero" />
        <div className="absolute inset-0 pattern-bg" />
        
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8 py-14 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Content */}
            <div>
              {/* Category Badge */}
              <span className="category-badge mb-6 inline-block">
                {newsletter.category}
              </span>

              {/* Title */}
              <h1 className="font-display text-3xl lg:text-4xl xl:text-5xl text-foreground mb-6 leading-tight">
                {newsletter.title}
              </h1>

              {/* Date */}
              <p className="text-sm text-muted-foreground">
                Published {formatDate(newsletter.date)}
              </p>
            </div>

            {/* Featured Image */}
            <div className="newsletter-image rounded-2xl overflow-hidden shadow-2xl border border-border/50">
              <img 
                src={newsletter.image} 
                alt={newsletter.title}
                className="w-full h-64 lg:h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="bg-background relative">
        <div className="absolute inset-0 pattern-bg opacity-20" />
        
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
          {/* Overview */}
          <div className="mb-14">
            <p className="text-xl text-foreground leading-relaxed">
              {newsletter.overview}
            </p>
          </div>

          {/* Info Grid */}
          <div className="grid md:grid-cols-3 gap-5 mb-16">
            {/* Who it's for */}
            <div className="info-card p-6 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="icon-container w-10 h-10 rounded-lg flex items-center justify-center">
                  <Users className="h-5 w-5 text-foreground" />
                </div>
                <h3 className="font-semibold text-foreground text-sm uppercase tracking-wide">
                  Who It's For
                </h3>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {newsletter.whoFor}
              </p>
            </div>

            {/* What problem it solves */}
            <div className="info-card p-6 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="icon-container w-10 h-10 rounded-lg flex items-center justify-center">
                  <Lightbulb className="h-5 w-5 text-foreground" />
                </div>
                <h3 className="font-semibold text-foreground text-sm uppercase tracking-wide">
                  What It Solves
                </h3>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {newsletter.problemSolves}
              </p>
            </div>

            {/* Why it matters */}
            <div className="info-card p-6 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="icon-container w-10 h-10 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-foreground" />
                </div>
                <h3 className="font-semibold text-foreground text-sm uppercase tracking-wide">
                  Why It Matters
                </h3>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {newsletter.whyMatters}
              </p>
            </div>
          </div>

          {/* PDF Section */}
          <div className="mb-16">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="icon-container w-12 h-12 rounded-xl flex items-center justify-center">
                  <FileText className="h-6 w-6 text-foreground" />
                </div>
                <h2 className="font-display text-2xl text-foreground">
                  Full Newsletter
                </h2>
              </div>
              <a
                href={newsletter.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary px-5 py-3 rounded-xl text-sm font-semibold inline-flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Download PDF
              </a>
            </div>

            {/* PDF Viewer with loading state and fallbacks */}
            <PDFViewer pdfUrl={newsletter.pdfUrl} title={newsletter.title} />
          </div>

          {/* Video Section */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-6">
              <div className="icon-container w-12 h-12 rounded-xl flex items-center justify-center">
                <Play className="h-6 w-6 text-foreground" />
              </div>
              <h2 className="font-display text-2xl text-foreground">
                Video Walkthrough
              </h2>
            </div>

            <div className="video-container border border-border">
              <iframe
                src={`https://www.youtube.com/embed/${newsletter.videoId}?rel=0&modestbranding=1`}
                title={`${newsletter.title} - Video Walkthrough`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Related Newsletters */}
      <section className="bg-card border-t border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-14">
          <h2 className="font-display text-2xl text-foreground mb-8">
            Related Newsletters
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            {newsletters
              .filter((n) => n.id !== newsletter.id && n.category === newsletter.category)
              .slice(0, 2)
              .map((related) => (
                <Link
                  key={related.id}
                  to={`/newsletter/${related.id}`}
                  className="feature-card rounded-2xl group overflow-hidden"
                >
                  {/* Related Newsletter Image */}
                  <div className="newsletter-image h-40 overflow-hidden">
                    <img 
                      src={related.image} 
                      alt={related.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <span className="category-badge mb-4 inline-block">
                      {related.category}
                    </span>
                    <h3 className="font-semibold text-foreground mb-3 group-hover:text-[hsl(38,70%,60%)] transition-smooth">
                      {related.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {related.summary}
                    </p>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsletterDetail;
